import React, { useRef, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Image } from 'expo-image';
import { BlurredExpoImage } from '@/components/ui/BlurredExpoImage';
import { LOCKED_CONTENT_BLUR } from '@/constants/lockedContentBlur';
import { router } from 'expo-router';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';
import { Card } from '@/components/gluestack/card';
import { Box } from '@/components/gluestack/box';
import { HStack } from '@/components/gluestack/hstack';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { Photo } from '@/models/Photo';
import {
  ImageViewerTrigger,
  ImageViewerTriggerWithRef,
  type ImageViewerTriggerHandle,
} from '@/components/ui/image-viewer/ImageViewerTrigger';
import { cssInterop } from 'nativewind';
import LikesCommentsOverlay from '@/components/domain/photos/LikesCommentsOverlay';
import { ProfileTooltip } from '@/components/domain/profile/ProfileTooltip';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { PresenceIndicator } from '@/components/domain/members/PresenceIndicator';
import { SquareLock02Icon } from '@hugeicons-pro/core-solid-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { getMemberPath } from '@/constants/routes';
import { ProtectedImageContainer } from '@/components/ui/ProtectedImageContainer';

cssInterop(Image, { className: 'style' });

interface PhotoCardProps {
  photo?: Photo;
  showLikesComments?: boolean;
  getPhotosForCarousel?: () => Photo[] | undefined;
  disableImageViewer?: boolean;
  showProfileTooltip?: boolean;
}

const PhotoCardComponent = ({
  photo,
  showLikesComments = false,
  getPhotosForCarousel,
  disableImageViewer = false,
  showProfileTooltip = false,
}: PhotoCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<ImageViewerTriggerHandle>(null);
  const isLargeScreen = useIsLargeScreen();
  const canShowLikesComments = showLikesComments && Platform.OS === 'web' && isLargeScreen;
  const canShowProfileTooltip = showProfileTooltip && !!photo?.Member?.UserName;
  if (!photo) {
    return <PhotoCardSkeleton showLikesComments={canShowLikesComments} />;
  }

  const shouldUseRefTrigger = photo.IsLocked && Platform.OS === 'web';
  const imageViewerUri = photo.IsLocked ? photo.IconUrl : (photo.FullSizeUrl ?? photo.IconUrl);
  const imageContent = (
    <BlurredExpoImage
      source={{ uri: photo.IconUrl }}
      className="aspect-square w-full rounded-lg"
      contentFit="cover"
      recyclingKey={photo.IconUrl}
      blur={photo.IsLocked ? LOCKED_CONTENT_BLUR : undefined}
    />
  );

  return (
    <Card
      size="sm"
      className="p-0 web:transition-transform web:duration-200 web:ease-in-out web:hover:scale-105">
      <ProtectedImageContainer
        className="relative"
        // Web-only hover handlers (use spread to avoid TS RN types complaining on native)
        {...(canShowLikesComments
          ? ({
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            } as any)
          : {})}>
        {disableImageViewer ? (
          <BlurredExpoImage
            source={{ uri: photo.IconUrl }}
            className="aspect-square w-full rounded-lg"
            contentFit="cover"
            recyclingKey={photo.IconUrl}
            blur={photo.IsLocked ? LOCKED_CONTENT_BLUR : undefined}
          />
        ) : shouldUseRefTrigger ? (
          <ImageViewerTriggerWithRef
            ref={triggerRef}
            uri={imageViewerUri}
            photo={photo}
            getPhotos={getPhotosForCarousel}
            allowImageDownload={!!photo.Member?.IsEvent}>
            {imageContent}
          </ImageViewerTriggerWithRef>
        ) : (
          <ImageViewerTrigger
            uri={imageViewerUri}
            photo={photo}
            getPhotos={getPhotosForCarousel}
            allowImageDownload={!!photo.Member?.IsEvent}>
            {imageContent}
          </ImageViewerTrigger>
        )}

        {photo.IsLocked && (
          <View
            className="absolute inset-0 items-center justify-center rounded-lg bg-black/30"
            pointerEvents={Platform.OS === 'web' ? 'auto' : 'none'}
            {...(Platform.OS === 'web'
              ? ({
                  onClick: () => !disableImageViewer && triggerRef.current?.open(),
                } as any)
              : {})}>
            <HugeIconsWrapper
              icon={SquareLock02Icon}
              size={48}
              className="text-white/70"
            />
          </View>
        )}

        {canShowLikesComments ? (
          <View className="absolute inset-0 z-10" pointerEvents="box-none">
            {canShowProfileTooltip ? (
              <HStack
                className="absolute left-0 right-0 items-center justify-center"
                style={{ top: 12, opacity: isHovered ? 1 : 0 }}>
                <ProfileTooltip member={photo.Member} isActive placement="top left">
                  <Pressable onPress={() => router.push(getMemberPath(photo.Member) as any)}>
                    <BlurView
                      intensity={80}
                      tint="dark"
                      className="overflow-hidden rounded-full"
                      {...(Platform.OS === 'web'
                        ? ({ pointerEvents: isHovered ? 'auto' : 'none' } as any)
                        : {})}>
                      <HStack className="items-center px-3 py-1.5 gap-1.5">
                        <Text bold className="text-white" isTruncated>{photo.Member.UserName}</Text>
                        <PresenceIndicator member={photo.Member} />
                      </HStack>
                    </BlurView>
                  </Pressable>
                </ProfileTooltip>
              </HStack>
            ) : null}
            <HStack
              className="absolute left-0 right-0 items-center justify-center"
              style={{ bottom: 12, opacity: isHovered ? 1 : 0 }}>
              <BlurView
                intensity={80}
                tint="dark"
                className="overflow-hidden rounded-full"
                {...(Platform.OS === 'web'
                  ? ({ pointerEvents: isHovered ? 'auto' : 'none' } as any)
                  : {})}>
                <LikesCommentsOverlay
                  photo={photo}
                  iconSize={20}
                  commentIconSize={18}
                  textSize="sm"
                  iconClassName="text-white"
                  disabled={photo.IsLocked}
                />
              </BlurView>
            </HStack>
          </View>
        ) : null}
      </ProtectedImageContainer>
    </Card>
  );
};

export const PhotoCard = React.memo(PhotoCardComponent);

function PhotoCardSkeleton({ showLikesComments = false }: { showLikesComments?: boolean }) {
  return (
    <Card size="sm" className="p-0">
      <Box className="aspect-square w-full">
        <Box className="h-full w-full animate-pulse rounded-lg bg-background-200" />
      </Box>
      {showLikesComments && (
        <VStack className="px-3 py-2">
          <Box className="h-5 w-20 animate-pulse rounded bg-background-200" />
        </VStack>
      )}
    </Card>
  );
}
